/**
 * @file        src/lib/tariffs/reference/lookup.ts
 * @description Query API for the PP 1291 (§I) recycling reference — revision selection by date and
 *              kW-exact coefficient lookup. Pure: no I/O beyond the static import, no Date.now().
 * @rule        Coefficients live in the JSON, never in TS. Editing numbers here breaks the
 *              provenance chain recorded in the JSON.
 * @rule        Indexation is NEVER computed. Every revision is a column of the decree text, stored
 *              explicitly — x1.2 is wrong in 1 of 160 cells (96,11 -> published 115,34, not 115,332).
 * @universal   knowledge/universals/tools.md#recycling-tariff-reference-pp-1291-section-i
 */

import { fail, getBrackets, reference, REVISION_KEYS } from './validation';
import type {
  AgeColumn,
  IceVolumeBlockKey,
  ParagraphKey,
  RecyclingProvenance,
  RevisionKey,
} from './types';

// === REVISION SELECTION ================================================

/** Normalise a Date or ISO-ish string to a `YYYY-MM-DD` string for revision-range comparison. */
function toIsoDate(date: Date | string): string {
  return typeof date === 'string' ? date.slice(0, 10) : date.toISOString().slice(0, 10);
}

/**
 * Pick the revision whose [validFrom, validTo] contains `date`.
 * @important Throws (naming the date) if no revision matches — it does NOT fall back to the newest.
 *            A fee computed against the wrong revision is a wrong fee.
 * @param date a Date or `YYYY-MM-DD` string
 * @returns the matching revision key
 */
export function resolveRevision(date: Date | string): RevisionKey {
  const iso = toIsoDate(date);
  for (const key of REVISION_KEYS) {
    const rev = reference.revisions[key];
    if (iso >= rev.validFrom && iso <= rev.validTo) return key;
  }
  return fail('resolveRevision', `no revision covers date '${iso}'`);
}

// === VOLUME BLOCK ======================================================

/**
 * Map an ICE engine displacement to its Section I volume block.
 * @input  cc — рабочий объём ДВС, см³
 * @output the volume block key
 * @important Upper bounds are INCLUSIVE, as in the decree headings: «не более 1000 куб. см»,
 *            «свыше 1000 куб. см, но не более 2000 куб. см», … So 1000 cc → upTo1000, 2000 → 1000–2000,
 *            3000 → 2000–3000, 3500 → 3000–3500. EV paragraphs (1 & 3) have no volume block.
 */
export function volumeBlockForCc(cc: number): IceVolumeBlockKey {
  if (cc <= 1000) return 'upTo1000';
  if (cc <= 2000) return 'from1000to2000';
  if (cc <= 3000) return 'from2000to3000';
  if (cc <= 3500) return 'from3000to3500';
  return 'over3500';
}

// === COEFFICIENT LOOKUP ================================================

/**
 * Look up a recycling coefficient by exact kW within a given revision.
 * @important The winning bracket is the FIRST one whose upper bound is at or above `kw` — the upper
 *            bound is INCLUSIVE (`kw <= kwTo`) and NO rounding is applied to `kw` at any point. Per
 *            the decree, rounding down is not permitted (117.68 → preferential, 117.69 → full). The
 *            lower bound (`kwFrom`) is deliberately NOT tested: hp→kW conversion produces continuous
 *            values that fall between the decree's discrete 0.01 kW steps (e.g. 130 hp = 95.61495 kW
 *            sits in the 0.005 kW gap between the 73,56–95,61 and 95,62–117,68 brackets). Testing
 *            `kwFrom` would leave those in-between values unmatched. Because brackets are validated
 *            (validation.ts) as contiguous, ascending, first starting at kwFrom 0, and last ending
 *            in `kwTo === null`, scanning in order and taking the first bracket that can still hold
 *            the value is exact for any kw ≥ 0.
 * @param revision    revision key (see resolveRevision / REVISION_KEYS)
 * @param paragraph   which Section I paragraph
 * @param volumeBlock ICE volume block, or `null` for the EV paragraphs (1 & 3)
 * @param kw          engine power in kilowatts, used as given
 * @param ageColumn   `new` or `over3`
 * @returns the coefficient; throws (naming the path) if the revision or bracket is not found
 */
export function findCoefficientKw(
  revision: RevisionKey,
  paragraph: ParagraphKey,
  volumeBlock: IceVolumeBlockKey | null,
  kw: number,
  ageColumn: AgeColumn,
): number {
  const rev = reference.revisions[revision];
  if (!rev) fail('revisions', `unknown revision '${revision}'`);
  const brackets = getBrackets(rev, paragraph, volumeBlock);
  // @rule Do NOT re-add a `kw >= b.kwFrom` lower-bound test. It looks like a missing safety check,
  //       but it re-opens the 0.005 kW gaps between the decree's discrete brackets: 130 / 310 / 370
  //       / 430 hp convert to kW values that sit between two brackets and would throw again. The
  //       upper-bound-only scan over contiguous ascending brackets already selects exactly one.
  const bracket = brackets.find((b) => b.kwTo === null || kw <= b.kwTo);
  if (!bracket) {
    fail(`${revision}.${paragraph}${volumeBlock ? `.${volumeBlock}` : ''}`, `no bracket contains kw=${kw}`);
  }
  return bracket[ageColumn];
}

// === EXPORTS ===========================================================

/** Label of each revision, keyed by revision key. */
export const REVISION_LABELS: Record<RevisionKey, string> = Object.fromEntries(
  REVISION_KEYS.map((key) => [key, reference.revisions[key].label]),
);

/** Provenance passport — where the numbers came from and how they were verified. */
export const provenance: RecyclingProvenance = reference.provenance;

/** The base decree these coefficients derive from (from provenance.decree). */
export const REFERENCE_REVISION: string = reference.provenance.decree;

/** Базовая ставка утильсбора M1, ₽ — из normativeRules.baseRateRub (единственное объявление). */
export const RECYCLING_BASE_RUB: number = reference.normativeRules.baseRateRub;
